<script>
// Functions
import numeral from 'numeral'
import Joi from '@hapi/joi'
import { back } from '@/utils/navigation'
import { mapGetters } from 'vuex'

// Components
import {
  ActionBar,
  ActionBarTitle,
  //ActionBarButton
} from '@/components/ui/action-bar'
import { PageContainer } from '@/components/page/page-container'
import { AppContainer } from '@/components/ui/container'
import { ClientProfileDisplay } from '@/components/page/client-profile'
import {
  PaymentList,
  PaymentListItem,
  PaymentListDivider
} from '@/components/page/payment-list'
import { PaymentNote } from '@/components/page/payment-note'
import {
  PaymentSummary,
  PaymentSummaryItem
} from '@/components/page/payment-summary'
import {
  PaymentMethod,
  PaymentMethodItem
} from '@/components/page/payment-method'
import { PaymentEvidence } from '@/components/page/payment-evidence'
import { showPrintDialog } from '@/components/dialog/receipt'
import Layout from './Layout'
import Info from './Info'
import TotalWeight from './TotalWeight'
import Evidence from './Evidence'
import EvidenceItem from './EvidenceItem'
import PaymentOverview from './PaymentOverview'
import { usePaymentMethod, usePaymentMethodMethod } from '@/use/payment'
import { useLoading } from '@/use/loading'

export default {
  data() {
    return {
      methods: [
        usePaymentMethod({
          method: usePaymentMethodMethod({
            id: 1,
            name: 'เงินสด',
            isFeeFree: true
          })
        })
      ],
      evidences: []
    }
  },

  computed: {
    ...mapGetters('pawn', ['customer', 'form']),

    totalAmount() {
      if (!this.form) return 0

      return this.form.items.reduce((prev, curr) => prev + curr.amount, 0)
    },

    totalWeight() {
      if (!this.form) return 0

      return this.form.items.reduce((prev, curr) => prev + curr.weight, 0)
    },

    totalPaid() {
      return this.methods.reduce((prev, curr) => prev + curr.amount, 0)
    },

    change() {
      return this.totalPaid - this.totalAmount
    }
  },

  created() {
    const form = this.$store.getters['pawn/paymentForm']

    if (form) {
      const { methods, evidences } = form

      this.methods = methods
      this.evidences = evidences
    }
  },

  methods: {
    commit() {
      this.$store.commit('pawn/SET_PAYMENT_FORM', {
        methods: this.methods,
        evidences: this.evidences
      })
    },

    back() {
      this.commit()
      back({ name: 'PawnCreate' })
    },

    async confirm() {
      const validate = async () => {
        const schema = Joi.object({
          methods: Joi.array()
            .items(
              Joi.object({
                method: Joi.object(),
                amount: Joi.number()
              })
            )
            .min(1)
        })

        try {
          await schema.validateAsync(this.$data, { allowUnknown: true })
        } catch (e) {
          throw new Error('กรุณาตรวจสอบข้อมูลชำระเงิน')
        }
      }

      const checkChange = async () => {
        if (this.change < 0) throw new Error('ยอดเงินคืนไม่ครบ')
      }

      const showPrint = async (id, change) => {
        const action = await showPrintDialog({
          change: change
        })

        switch (action) {
          case 'slip':
            return window.open(
              this.$router.resolve({
                name: 'PawnPrint',
                params: { id }
              }).href
            )
        }
      }

      try {
        await validate()

        await checkChange()

        const loading = useLoading()

        try {
          const {
            pawn_pos: { id }
          } = await this.$store.dispatch('pawn/createReceipt', this.$data)

          loading.close()

          try {
            await showPrint(id, this.change)
          } catch (e) {
            //
          } finally {
            this.$store.dispatch('pawn/reset')

            this.$router.push({ name: 'Pawn' })
          }
        } catch (e) {
          this.$notify.error(e.message)
        } finally {
          loading.close()
        }
      } catch (e) {
        this.$message.warning(e.message)
      }
    },

    addMethod() {
      this.methods = [...this.methods, usePaymentMethod()]
    },

    removeMethod(index) {
      this.methods = this.methods.filter(
        (item, itemIndex) => itemIndex !== index
      )
    },

    handlePaymentMethodAdd() {
      this.addMethod()
    },

    handlePaymentMethodRemove(index) {
      this.removeMethod(index)
    },

    handlePaymentEvidenceChange(value) {
      this.evidences = value
    }
  },

  render() {
    return (
      <div>
        <ActionBar>
          <el-button slot="action" onClick={this.back}>
            กลับ
          </el-button>
          <el-button slot="action" type="primary" onClick={this.confirm}>
            ยืนยัน
          </el-button>
          <ActionBarTitle>ขายฝาก บันทึกรายจ่าย</ActionBarTitle>
        </ActionBar>
        <PageContainer>
          <AppContainer>
            <Layout>
              <el-card header="รายการ">
                {this.customer && (
                  <ClientProfileDisplay
                    avatar={this.customer.avatar}
                    name={`${this.customer.firstName} ${this.customer.lastName}`.trim()}
                    tel={this.customer.tel}
                  />
                )}

                {this.form && [
                  <Info
                    ticketNo={this.form.ticketNo}
                    date={this.form.date}
                    dueDate={this.form.dueDate}
                    interest={this.form.interest}
                    interestPerMonth={this.form.interestPerMonth}
                    style={{ marginTop: '1.5rem' }}
                  />,

                  <PaymentList
                    header="รายการรับขายฝาก"
                    style={{ marginTop: '1.5rem' }}
                  >
                    {this.form.items.map((item, index) => (
                      <PaymentListItem
                        key={index}
                        title={item.percentage.name}
                        value={item.amount}
                      >
                        {numeral(item.percentage.percent).format('0,0%')} /
                        น้ำหนักทอง {numeral(item.weight).format('0,0.00')} บาท ,{' '}
                        {numeral(item.realWeight).format('0,0.00')} กรัม
                      </PaymentListItem>
                    ))}
                    <PaymentListDivider />
                    <PaymentListItem
                      title="รวม"
                      value={this.totalAmount}
                      style={{ marginBottom: '0.5rem' }}
                      scopedSlots={{
                        title: title => (
                          <span
                            style={{ fontSize: '1.125rem', fontWeight: 'bold' }}
                          >
                            {title}
                          </span>
                        )
                      }}
                    />
                    <li>
                      <TotalWeight value={this.totalWeight} />
                    </li>
                  </PaymentList>,

                  <PaymentNote style={{ marginTop: '1.5rem' }}>
                    {this.form.note}
                  </PaymentNote>,

                  <Evidence style={{ marginTop: '1.5rem' }}>
                    {this.form.evidences.map((evidence, index) => (
                      <EvidenceItem key={index} src={evidence.url} />
                    ))}
                  </Evidence>
                ]}
              </el-card>
              <el-card header="ยอดเงินจ่ายออก">
                <PaymentOverview total={this.totalAmount} />

                <PaymentSummary style={{ marginTop: '1.5rem' }}>
                  <PaymentSummaryItem title="เงินคืน">
                    <span class={this.utilStyles.colorPrimary}>
                      ฿ {numeral(this.change).format('0,0.00')}
                    </span>
                  </PaymentSummaryItem>
                </PaymentSummary>

                <PaymentMethod
                  title="ช่องทางจ่ายให้ลูกค้า"
                  style={{ marginTop: '1.5rem' }}
                  onAdd={this.handlePaymentMethodAdd}
                >
                  {this.methods.map((item, index) => (
                    <PaymentMethodItem
                      key={index}
                      index={index}
                      method={item.method}
                      refCode={item.ref}
                      amount={item.amount}
                      fee={item.fee}
                      feeFree
                      on={{
                        'update:method': value => {
                          item.method = value
                        },
                        'update:refCode': value => {
                          item.ref = value
                        },
                        'update:amount': value => {
                          item.amount = value
                        },
                        'update:fee': value => {
                          item.fee = value
                        },
                        remove: this.handlePaymentMethodRemove
                      }}
                    />
                  ))}
                </PaymentMethod>

                <PaymentEvidence
                  value={this.evidences}
                  style={{ marginTop: '1.5rem' }}
                  onChange={this.handlePaymentEvidenceChange}
                />
              </el-card>
            </Layout>
          </AppContainer>
        </PageContainer>
      </div>
    )
  }
}
</script>

<style
  src="@/assets/scss/modules/utilities.module.scss"
  lang="scss"
  module="utilStyles"
></style>
